import {splitURL} from './dom';
export {shallowEqual} from 'react-redux';

export function truncateURL(v) {
    if (!v) return null;
    if (typeof v !== "string") return null;
    let u = splitURL(v);
    if (u.origin === window.location.origin || u.origin === polyphony.getState().site_url) {
        return u.rootRelative;
    }
    return v;
}

export const dedupe = (ary, f = a => a) => {
    const fmapped = ary.map(x => f(x)); // avoid passing map args to f
    return ary.filter((x, i) => fmapped.indexOf(f(x)) === i);
};

export const async = f => new Promise(rslv => setTimeout(() => rslv(f()), 0));

export function hashCode(str) {
  return str.split('').reduce((prevHash, currVal) =>
    (((prevHash << 5) - prevHash) + currVal.charCodeAt(0))|0, 0); // eslint-disable-line no-extra-parens
}

export function setRef(ref, v) {
    if (!ref) return;

    if (typeof ref === 'function') {
        ref(v);
    } else {
        ref.current = v;
    }
}

export function combineRefs(refs) {
    return {
        '_current': null,
        set current(r) {
            this._current = r;
            for (const ref of refs) setRef(ref, r);
        },
        get current() {
            return this._current;
        }
    };
}

export const callOnce = f => {
    const x = {f};
    return (...args) => {
        if (x.f) x.f(...args);
        x.f = null;
    };
};

export const uniqueIdFor = (() => {
    let currentId = 0;
    const map = new Map();

    return object => {
        if (!map.has(object)) {
            map.set(object, ++currentId);
        }

        return map.get(object);
    };
})();

export const memoized = f => {
    const memo = {};
    return function() {
        let args = [...arguments];
        let key = args.map(x => `${uniqueIdFor(x)}`).join('|');
        if (memo[key]) {
            return memo[key];
        }
        let v = f.apply(null, args);
        memo[key] = v;
        return v;
    };
};

export const isPostEditor = () => window.wp && window.wp.editPost;
export const isAdmin = () => window.wp && (window.wp.mce || window.wp.updates);

const _uuidv4Replace = c => {
    let r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); // eslint-disable-line no-extra-parens
};

export const uuidv4 = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, _uuidv4Replace);

export const joinURIComponents = cmps =>
    Object.entries(cmps).map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v)).join('&');

export const secondArg = (d, p) => p;

export const constF = x => (() => x); // eslint-disable-line

export const multiArg = v => Array.isArray(v) ? [...v].sort().join('__') : v;

export const safeCall = function() {
    let [fn, ...args] = Array.from(arguments);
    if (fn) {
        return fn(...args);
    }
    return undefined;
};

export function times(num, v) {
    let a = [];
    for (let i = 0; i < num; i++) a.push(v);
    return a;
}

export function lcm(n1, n2) {
    let large = Math.max(n1, n2);
    let small = Math.min(n1, n2);

    let i = large;
    while (i % small !== 0) i += large;
    return i;
}

export const formatNumber = n => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const createEvent = eventName => {
    if (typeof Event === 'function') {
        return new Event(eventName);
    }
    let event = document.createEvent('Event');
    event.initEvent(eventName, true, true);
    return event;
};

// A wrapper for setTimeout that doesn't use magical globals.
// This function was written to DRY up using setTimeout in useEffect().
export const executeAfter = (fn, timeout) => {
    const t = setTimeout(fn, timeout);
    return () => clearTimeout(t);
};

// WordPress's registerBlockType()'s save function
// is not a real React component. It's a function that
// takes a div, and expects plain react elements. Memos
// and forward refs break this contract.
export const unwrapComponent = c => {
    const type = c.$$typeof ? c.$$typeof.toString() : "";
    if (type === 'Symbol(react.memo)') {
        return unwrapComponent(c.type);
    }

    if (type === 'Symbol(react.forward_ref)') {
        return s => c.render(s, null);
    }
    return c;
};

export default {
    lcm,
    times,
    joinURIComponents,
    uuidv4,
    isPostEditor,
    isAdmin,
    secondArg,
    constF,
    multiArg,
    safeCall,
    formatNumber,
    createEvent,
    executeAfter,
    unwrapComponent
};

